import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Lock, ShieldCheck, Loader2, Eye, EyeOff } from 'lucide-react';

export default function SetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser, setToken, api } = useAuth();

  const token = params.get('token') || '';
  const email = params.get('email') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token || !email) {
      setError('Invalid invite link. Please check your email for the correct link.');
    }
  }, [token, email]);

  /* Defensive: if the user already finished setting their password (JWT
     in localStorage matches URL email), bounce them straight to the app
     instead of letting them re-submit a consumed invite token. */
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const storedToken = (typeof window !== 'undefined') ? localStorage.getItem('token') : null;
      if (!storedToken) return;
      try {
        const res = await api.get('/api/auth/me');
        if (cancelled) return;
        const me = res?.data;
        if (me?.email && email && me.email.toLowerCase() === email.toLowerCase()) {
          if (setUser) setUser(me);
          navigate(me.pending_stripe_setup ? '/billing-setup' : '/', { replace: true });
        }
      } catch (_e) { /* stale token — ignore */ }
    };
    run();
    return () => { cancelled = true; };
  }, [api, email, navigate, setUser]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/api/auth/set-password', { token, email, password });
      const { token: jwt, user, pending_stripe_setup } = res.data;

      localStorage.setItem('token', jwt);
      if (setToken) setToken(jwt);
      if (setUser) setUser(user);

      toast.success('Password set! Adding your payment method next...');

      // Mandatory CC-on-file step
      if (pending_stripe_setup) {
        navigate('/billing-setup', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to set password';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{ background: '#0a0b0f' }}
      data-testid="set-password-page"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-teal-400/10 border border-teal-400/20 mb-4">
            <ShieldCheck className="text-teal-400" size={26} />
          </div>
          <h1 className="text-3xl font-semibold text-white">Set Your Password</h1>
          <p className="text-sm text-slate-400 mt-2">
            Welcome to Voxinity AI. Create a secure password for{' '}
            <span className="text-teal-300">{email || 'your account'}</span>.
          </p>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-6">
          <form onSubmit={submit} className="space-y-4" data-testid="set-password-form">
            <div>
              <label className="block text-sm text-slate-300 mb-1.5">New Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                <input
                  type={show ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 rounded-lg bg-[#0f1116] border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-teal-400/60"
                  placeholder="At least 8 characters"
                  required
                  minLength={8}
                  data-testid="set-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  tabIndex={-1}
                >
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-1.5">Confirm Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                <input
                  type={show ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-[#0f1116] border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-teal-400/60"
                  placeholder="Re-enter password"
                  required
                  minLength={8}
                  data-testid="set-password-confirm-input"
                />
              </div>
            </div>

            {error && (
              <div className="rounded-lg border border-rose-400/30 bg-rose-400/10 p-3 text-sm text-rose-300" data-testid="set-password-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !token || !email}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-teal-400 text-[#0a0b0f] font-medium hover:bg-teal-500 transition-colors disabled:opacity-50"
              data-testid="set-password-submit-btn"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <ShieldCheck size={18} />}
              {loading ? 'Setting password…' : 'Set Password & Continue'}
            </button>
          </form>

          <p className="text-xs text-slate-500 mt-4 text-center">
            After this, you'll add a payment method to activate your workspace.
          </p>
        </div>
      </div>
    </div>
  );
}
